import { useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';

/**
 * A searchable single-select over a list of entities. Optionally offers a
 * "create new" action at the bottom of the list.
 *
 * items: [{ value, label, sublabel? }]
 * value: currently-selected value (or '')
 * onChange(value): called when an item is picked
 * onCreate(): optional — called when the "create new" row is chosen
 */
const EntityCombobox = ({
  items = [],
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  createLabel,
  onCreate,
  disabled = false,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = items.find((i) => i.value === value);

  // Manual filtering (cmdk's built-in filter is disabled below) so the "create
  // new" row is never hidden — it must stay reachable exactly when the search
  // matches no existing item, which is when you want to create one.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((i) => `${i.label} ${i.sublabel || ''}`.toLowerCase().includes(q))
    : items;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown size={16} className="opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            {filtered.length === 0 && !onCreate && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {filtered.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={() => { onChange(item.value); setOpen(false); setSearch(''); }}
                  data-testid={testId ? `${testId}-option-${item.value}` : undefined}
                >
                  <Check size={16} className={cn('mr-2', value === item.value ? 'opacity-100' : 'opacity-0')} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.sublabel && <span className="ml-2 text-xs text-muted-foreground">{item.sublabel}</span>}
                </CommandItem>
              ))}
              {onCreate && (
                <CommandItem
                  value="__create__"
                  onSelect={() => { setOpen(false); const s = search.trim(); setSearch(''); onCreate(s); }}
                  data-testid={testId ? `${testId}-create` : undefined}
                  className="text-primary"
                >
                  <Plus size={16} className="mr-2" />
                  {search.trim() ? `${createLabel || 'Create'} "${search.trim()}"` : (createLabel || 'Create new')}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default EntityCombobox;
